import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../config';
import {
  describeDungeonResults,
  type DungeonRunState,
} from '../../domain';
import {
  createSaveStore,
  updateSavedGameRun,
} from '../../persistence/saveStore';
import {
  SAVE_SCHEMA_VERSION,
  createDefaultPermanentSave,
  type SettlementApplied,
} from '../../persistence/schema';
import {
  markAwaitingSettlement,
  outcomeFromDungeon,
  settleRun,
} from '../../persistence/settlement';
import { addPrimaryButton } from '../ui/hubUi';

export interface DungeonResultSceneData {
  dungeonRun: DungeonRunState;
}

/**
 * Victory / defeat summary after a mini-dungeon ends.
 * Settles the run exactly once (idempotent), then returns to the village.
 */
export class DungeonResultScene extends Phaser.Scene {
  private run!: DungeonRunState;
  private applied: SettlementApplied | null = null;
  private outcomeLabel: 'victory' | 'death' = 'victory';

  constructor() {
    super('DungeonResult');
  }

  init(data: DungeonResultSceneData): void {
    this.run = data.dungeonRun;
    this.applied = null;
  }

  create(): void {
    const cx = GAME_WIDTH / 2;
    const won = this.run.outcome === 'victory';
    this.outcomeLabel = won ? 'victory' : 'death';

    this.settleIfNeeded();

    this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x0b1020).setOrigin(0);

    this.add
      .text(cx, 100, won ? 'VICTORY' : 'DEFEAT', {
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: '40px',
        color: won ? '#e8d5a3' : '#c45c5c',
        stroke: '#3a2a10',
        strokeThickness: 3,
      })
      .setOrigin(0.5);

    this.add
      .text(cx, 150, this.run.dungeonName, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#9aa3c0',
      })
      .setOrigin(0.5);

    const lines = describeDungeonResults(this.run);
    lines.forEach((line, index) => {
      this.add
        .text(cx, 200 + index * 26, line, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          color: index === 0 ? '#f2e6c8' : '#9aa3c0',
          align: 'center',
          wordWrap: { width: GAME_WIDTH - 48 },
        })
        .setOrigin(0.5);
    });

    const awardLines = this.awardSummaryLines();
    awardLines.forEach((line, index) => {
      this.add
        .text(cx, 320 + index * 24, line, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '13px',
          color: line.startsWith('Lost') ? '#c45c5c' : '#7ec8a3',
          align: 'center',
          wordWrap: { width: GAME_WIDTH - 48 },
        })
        .setOrigin(0.5);
    });

    addPrimaryButton(this, cx, GAME_HEIGHT - 100, 'Return to Village', () => {
      this.clearSettledRunSlot();
      this.scene.start('Village');
    });
  }

  private settleIfNeeded(): void {
    const store = createSaveStore();
    const previous = store.load();
    const permanent =
      previous?.permanent ?? createDefaultPermanentSave();
    let run = previous?.run;
    if (!run || run.dungeon.dungeonId !== this.run.dungeonId) {
      // Fallback: wrap the scene payload if save is missing the run.
      const wrapped = updateSavedGameRun(
        previous,
        this.run,
        previous?.rngState ?? this.run.seed,
      );
      run = wrapped.run!;
    } else {
      run = { ...run, dungeon: this.run };
    }

    const outcome = outcomeFromDungeon(run) ?? this.outcomeLabel;
    const marked = markAwaitingSettlement(run, outcome);
    const settled = settleRun(permanent, marked, outcome);
    this.applied = settled.run.settlement?.applied ?? null;

    store.save({
      version: SAVE_SCHEMA_VERSION,
      savedAt: Date.now(),
      rngState: previous?.rngState ?? this.run.seed,
      permanent: settled.permanent,
      run: settled.run,
    });
  }

  private awardSummaryLines(): string[] {
    const a = this.applied;
    if (!a) {
      return ['Settlement pending…'];
    }
    if (a.outcome === 'victory') {
      const loot =
        a.lootItemIds.length > 0
          ? `Loot banked: ${a.lootItemIds.length} item(s)`
          : 'Loot: none this run';
      return [
        `Class XP banked: +${a.classXp}`,
        `General XP (clear): +${a.generalXp}`,
        loot,
        a.currencyGold > 0 || a.currencyMaterials > 0
          ? `Currency: +${a.currencyGold}g / +${a.currencyMaterials} mat`
          : 'Currency: none',
      ];
    }
    // death
    return [
      `Class XP banked: +${a.classXp}`,
      'Lost: completion General XP',
      'Lost: run loot & run currency',
    ];
  }

  private clearSettledRunSlot(): void {
    const store = createSaveStore();
    const previous = store.load();
    if (previous?.permanent) {
      store.save({
        version: SAVE_SCHEMA_VERSION,
        savedAt: Date.now(),
        rngState: previous.rngState,
        permanent: previous.permanent,
        run: null,
      });
    }
  }
}
